import { Link } from "@tanstack/react-router";
import { ChevronRight, ListChecks, Sparkles, ArrowRight, BookOpen, Clock } from "lucide-react";
import type { ForexGuide } from "@/lib/forex-guides";

/**
 * Shared renderer for every guide at /guides/<slug>.
 *
 * Designed for AI citation:
 * - "AI Answer" block at the top, written to be lifted verbatim by
 *   ChatGPT / Perplexity / Gemini / Google AI Overview.
 * - Steps render as HowTo JSON-LD (when present).
 * - FAQs render as FAQPage JSON-LD.
 * - Article + BreadcrumbList JSON-LD always emitted.
 *
 * JSON-LD is injected by the route's head() so it lives in the document
 * head, not the body.
 */
export function ForexGuideView({ guide }: { guide: ForexGuide }) {
  const breadcrumbs = [
    { name: "Home", url: "/" },
    { name: "Guides", url: "/guides" },
    { name: guide.heading ?? guide.title, url: `/guides/${guide.slug}` },
  ];

  return (
    <div className="min-h-screen bg-background">
      <main className="container mx-auto max-w-3xl px-6 py-12 md:py-20">
        {/* Breadcrumbs */}
        <nav aria-label="Breadcrumb" className="mb-8 text-sm text-muted-foreground">
          <ol className="flex flex-wrap items-center gap-1">
            {breadcrumbs.map((b, i) => {
              const last = i === breadcrumbs.length - 1;
              return (
                <li key={b.url} className="flex items-center gap-1">
                  {last ? (
                    <span aria-current="page" className="font-medium text-foreground">
                      {b.name}
                    </span>
                  ) : (
                    <Link to={b.url} className="hover:text-primary">
                      {b.name}
                    </Link>
                  )}
                  {!last && <ChevronRight className="h-3.5 w-3.5" aria-hidden />}
                </li>
              );
            })}
          </ol>
        </nav>

        <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
          <BookOpen className="h-3.5 w-3.5" aria-hidden /> {guide.category}
        </div>
        <h1 className="mb-4 text-4xl font-bold leading-tight md:text-5xl">
          {guide.heading ?? guide.title}
        </h1>
        <p className="mb-2 text-lg leading-relaxed text-muted-foreground">{guide.intro}</p>
        <p className="mb-10 inline-flex items-center gap-2 text-xs text-muted-foreground">
          <Clock className="h-3.5 w-3.5" aria-hidden /> Updated{" "}
          {new Date(guide.updatedAt).toLocaleDateString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
          })}
        </p>

        {/* AI ANSWER BLOCK — primary citation surface */}
        <section
          aria-label="Quick answer"
          className="mb-12 rounded-xl border border-primary/30 bg-gradient-to-br from-primary/10 via-primary/5 to-transparent p-6"
        >
          <div className="mb-2 flex items-center gap-2 text-primary">
            <Sparkles className="h-4 w-4" aria-hidden />
            <span className="text-xs font-semibold uppercase tracking-wide">Quick answer</span>
          </div>
          <p className="text-base leading-relaxed text-foreground">{guide.aiAnswer}</p>
        </section>

        {/* Key points */}
        <section className="mb-12" aria-labelledby="key-points-heading">
          <h2 id="key-points-heading" className="mb-4 text-2xl font-semibold">
            Key takeaways
          </h2>
          <ul className="space-y-2">
            {guide.keyPoints.map((p) => (
              <li
                key={p}
                className="rounded-md border-l-4 border-primary/60 bg-primary/5 px-4 py-2 text-sm text-foreground/90"
              >
                {p}
              </li>
            ))}
          </ul>
        </section>

        {/* Body paragraphs */}
        <section className="mb-12 space-y-4 text-foreground/90 leading-relaxed">
          {guide.body.map((para, i) => (
            <p key={i}>{para}</p>
          ))}
        </section>

        {/* Steps (optional) */}
        {guide.steps && guide.steps.length > 0 && (
          <section className="mb-12" aria-labelledby="steps-heading">
            <div className="mb-5 flex items-center gap-2">
              <ListChecks className="h-5 w-5 text-primary" aria-hidden />
              <h2 id="steps-heading" className="text-2xl font-semibold">
                Step-by-step
              </h2>
            </div>
            <ol className="space-y-3">
              {guide.steps.map((s, i) => (
                <li key={s.title} className="flex gap-4 rounded-lg border border-border bg-card p-4">
                  <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-primary/10 font-semibold text-primary">
                    {i + 1}
                  </span>
                  <div>
                    <h3 className="mb-1 font-semibold">{s.title}</h3>
                    <p className="text-sm leading-relaxed text-muted-foreground">{s.description}</p>
                  </div>
                </li>
              ))}
            </ol>
          </section>
        )}

        {/* FAQs */}
        <section className="mb-16" aria-labelledby="faq-heading">
          <h2 id="faq-heading" className="mb-6 text-3xl font-bold">
            Frequently asked questions
          </h2>
          <div className="space-y-6">
            {guide.faqs.map((f) => (
              <div key={f.q} className="border-b border-border pb-6">
                <h3 className="mb-2 text-lg font-semibold">{f.q}</h3>
                <p className="leading-relaxed text-muted-foreground">{f.a}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Related */}
        <section className="mb-16" aria-labelledby="related-heading">
          <h2 id="related-heading" className="mb-4 text-2xl font-semibold">
            Continue on ForexMind AI
          </h2>
          <ul className="grid gap-3 sm:grid-cols-2">
            {guide.relatedLinks.map((l) => (
              <li key={l.to}>
                <Link
                  to={l.to}
                  className="group flex items-center justify-between rounded-md border border-border bg-card p-3 transition-colors hover:border-primary hover:bg-primary/5"
                >
                  <span className="text-sm font-medium">{l.label}</span>
                  <ArrowRight
                    className="h-4 w-4 text-muted-foreground group-hover:text-primary"
                    aria-hidden
                  />
                </Link>
              </li>
            ))}
          </ul>
        </section>

        {/* Trust block */}
        <section className="rounded-xl border border-primary/20 bg-primary/5 p-6">
          <h2 className="mb-2 text-lg font-semibold">ForexMind AI — institutional-grade market intelligence</h2>
          <p className="mb-4 text-sm leading-relaxed text-muted-foreground">
            ForexMind AI runs an 11-agent council modelled on the styles of George Soros, Stanley
            Druckenmiller, Ray Dalio, Jim Simons, and other trading legends. Every signal is
            backed by a published confluence score, reflexivity gauge, and an Order Flow Intensity read (a BVC approximation of VPIN computed on candles — not true tick-level VPIN).
            Live track record at /performance.
          </p>
          <div className="flex flex-wrap gap-3 text-sm">
            <Link to="/signup" className="font-medium text-primary underline">
              Start free
            </Link>
            <Link to="/performance" className="font-medium text-primary underline">
              See live track record
            </Link>
            <Link to="/guides" className="font-medium text-primary underline">
              All guides
            </Link>
          </div>
        </section>
      </main>
    </div>
  );
}
